import { CheckOutlined } from "@ant-design/icons";
import { Divider, Flex, Skeleton, Typography } from "antd";
import { ItemInfo, ItemInfoBlock } from "components";
import { COMPANY_TYPES, PlatformDetails } from "interfaces";
import { observer } from "mobx-react-lite";
import { STATEMENT_LABELS } from "pages/BidInfoPage/BidInfoBlocks/BankStatements/getColumns";

import { useNotificationContext } from "providers";
import { useEffect, useMemo, useState } from "react";
import { useStores } from "stores";
import { CopyableText, CustomButton } from "ui";
import { formatWithDecimals, getCompanyType, requestWithNotify } from "utils";




const getInfo = (platformDetails: PlatformDetails) => {
  const registrationLabel =
    getCompanyType(platformDetails.companyRegistrationNumber) === COMPANY_TYPES.IP ? "ОГРНИП" : "ОГРН";

  const companyInfo = [
    {
      label: "Организация",
      text: <CopyableText children={platformDetails.companyName} />,
    },
    {
      label: "ИНН",
      text: <CopyableText children={platformDetails.inn} />,
    },
    {
      label: registrationLabel,
      text: <CopyableText children={platformDetails.companyRegistrationNumber} />,
    },
    {
      label: "КПП",
      text: <CopyableText children={platformDetails.kpp} />,
    },
  ];

  const bankAccountInfo = [
    {
      label: "Банк - получатель",
      text: <CopyableText children={platformDetails.defaultAccount.bankName} />,
    },
    {
      label: "БИК",
      text: <CopyableText children={platformDetails.defaultAccount.bik} />,
    },
    {
      label: "Кор. счет",
      text: <CopyableText children={platformDetails.defaultAccount.correspondentAccount} />,
    },
    {
      label: "Расчетный счет",
      text: <CopyableText children={platformDetails.defaultAccount.accountNumber} />,
    },
    {
      label: "Назначение платежа",
      text: <CopyableText children={platformDetails.paymentPurpose} />,
    },
  ];

  return { companyInfo, bankAccountInfo };
};

export const PlatformDetailsModal = observer(() => {
  const { nominalAccounts } = useStores();
  const { platformDetails } = nominalAccounts;

  const { openNotification } = useNotificationContext();

  const { companyInfo, bankAccountInfo } = useMemo(() => getInfo(platformDetails.data), [platformDetails.data]);

  const [copied, setCopied] = useState(false);

  const preparedValues = useMemo(() => {
    const registrationLabel =
      getCompanyType(platformDetails.data.companyRegistrationNumber) === COMPANY_TYPES.IP ? "ОГРНИП" : "ОГРН";

    let allValues = [
      `${STATEMENT_LABELS.companyName}: ${platformDetails.data.companyName}`,
      `${STATEMENT_LABELS.inn}: ${platformDetails.data.inn}`,
      `${registrationLabel}: ${platformDetails.data.companyRegistrationNumber}`,
      `${STATEMENT_LABELS.kpp}: ${platformDetails.data.kpp}`,
      `${STATEMENT_LABELS.bankName}: ${platformDetails.data.defaultAccount.bankName}`,
      `${STATEMENT_LABELS.bik}: ${platformDetails.data.defaultAccount.bik}`,
      `${STATEMENT_LABELS.correspondentAccount}: ${platformDetails.data.defaultAccount.correspondentAccount}`,
      `${STATEMENT_LABELS.accountNumber}: ${platformDetails.data.defaultAccount.accountNumber}`,
      `${STATEMENT_LABELS.paymentPurpose}: ${platformDetails.data.paymentPurpose}`,
    ];

    return allValues.filter(Boolean).join("\n").trim();
  }, [platformDetails.data]);

  const copyAllStatement = () => {
    navigator.clipboard.writeText(preparedValues).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  useEffect(() => {
    requestWithNotify({
      fetchFn: () => platformDetails.fetch(),
      openNotification: openNotification,
      delay: 0,
    });
  }, []);

  return platformDetails.statuses.loading ? (
    <Flex vertical gap="small">
      <Skeleton active />
      <Skeleton active />
    </Flex>
  ) : (
    <Flex vertical>
      <Divider className="margin-top-none" />
      <ItemInfoBlock items={companyInfo} oneColumn vertical={false} />
      <Divider className="margin-top-none margin-bottom-xs" />
      <ItemInfoBlock items={bankAccountInfo} oneColumn vertical={false} blockTitle="Банковские реквизиты получателя" />
      <Divider />
      <CustomButton
        className="full-width"
        variant="outlined"
        onClick={copyAllStatement}
        icon={copied ? <CheckOutlined /> : null}
        iconPosition="end"
      >
        {copied ? "Скопировано" : "Скопировать реквизиты"}
      </CustomButton>
    </Flex>
  );
});
